import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Flight as FlightIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  LocalAirport as LocalAirportIcon
} from '@mui/icons-material';
import SignaturePad from '../common/SignaturePad';
import BAUpload from '../compliance/BAUpload';

/**
 * CrewAssignments-part2.js
 * 
 * This file contains dialog components extracted from CrewAssignments.js.
 * This split was done to keep file sizes manageable (500-600 lines per file).
 * 
 * Dialogs:
 * 1. DetailDialog - Full assignment information view
 * 2. RespondDialog - Accept/Reject assignment
 * 3. BaFailureDialog - Report BA device issue
 * 4. BaUploadDialog - Upload BA test video
 * 5. SignatureDialog - Sign ATC clearance
 * 
 * Note: FlightLogDialog has been moved to CrewAssignments-part3.js
 */

// 1. Detail Dialog - Full Information View
export const DetailDialog = ({
  open,
  onClose,
  selectedAssignment,
  formatDateTime,
  getStatusColor
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
    <DialogTitle>
      Complete Assignment Details - Sector {selectedAssignment?.seqNo}
    </DialogTitle>
    <DialogContent>
      {selectedAssignment && (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <FlightIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Trip Information
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Charter Number"
                        secondary={selectedAssignment.tripId?.charterNumber || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Title"
                        secondary={selectedAssignment.tripId?.title || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Flight Type"
                        secondary={selectedAssignment.tripId?.flightType || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Wake Turbulence"
                        secondary={selectedAssignment.wakeTurbulence || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Flight Rules"
                        secondary={selectedAssignment.flightRules || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Client"
                        secondary={
                          selectedAssignment.tripId?.clientName ||
                          selectedAssignment.tripId?.clientContact?.email ||
                          'N/A'
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Trip Status"
                        secondary={
                          <Chip
                            label={selectedAssignment.tripId?.status || 'N/A'}
                            size="small"
                            color={getStatusColor(selectedAssignment.tripId?.status)}
                          />
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Compliance Requirements"
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label="BA Required"
                              size="small"
                              color="info"
                              sx={{ mr: 0.5 }}
                              title="Mandatory DGCA requirement"
                            />
                            <Chip
                              label="ATC Required"
                              size="small"
                              color="info"
                              title="Mandatory DGCA requirement"
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <LocalAirportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Aircraft & Crew
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Aircraft"
                        secondary={
                          selectedAssignment.aircraftId
                            ? `${selectedAssignment.aircraftId.registration} - ${selectedAssignment.aircraftId.manufacturer} ${selectedAssignment.aircraftId.model}`
                            : 'Not assigned'
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Assigned Pilots"
                        secondary={
                          selectedAssignment.assignedPilots?.length > 0
                            ? selectedAssignment.assignedPilots
                                .map(
                                  (p) =>
                                    `${p.firstName} ${p.lastName} (${p.email})`
                                )
                                .join(', ')
                            : 'No pilots assigned'
                        }
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {(selectedAssignment.requirements || selectedAssignment.weatherMediaId || (selectedAssignment.tripKitMediaIds && selectedAssignment.tripKitMediaIds.length) || selectedAssignment.tripKitMediaId) && (
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Flight Requirements
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <List dense>
                      {((selectedAssignment.requirements?.fuelKg != null && selectedAssignment.requirements.fuelKg !== '') || (selectedAssignment.requirements?.fuelLitres != null && selectedAssignment.requirements.fuelLitres !== '') || selectedAssignment.requirements?.fuel) && (
                        <ListItem>
                          <ListItemText
                            primary="Planned Fuel Consumption"
                            secondary={[
                              selectedAssignment.requirements.fuelKg != null && selectedAssignment.requirements.fuelKg !== '' ? `${selectedAssignment.requirements.fuelKg} kg` : null,
                              (selectedAssignment.requirements.fuelLitres != null && selectedAssignment.requirements.fuelLitres !== '') ? `${selectedAssignment.requirements.fuelLitres} L` : (selectedAssignment.requirements.fuel != null && selectedAssignment.requirements.fuel !== '' ? `${selectedAssignment.requirements.fuel} L` : null)
                            ].filter(Boolean).join(', ')}
                          />
                        </ListItem>
                      )}
                      {selectedAssignment.requirements?.altitude && (
                        <ListItem>
                          <ListItemText
                            primary="Altitude"
                            secondary={`${selectedAssignment.requirements.altitude} ft`}
                          />
                        </ListItem>
                      )}
                      {selectedAssignment.requirements?.route && (
                        <ListItem>
                          <ListItemText
                            primary="Route"
                            secondary={selectedAssignment.requirements.route}
                          />
                        </ListItem>
                      )}
                      {selectedAssignment.requirements?.weather && (
                        <ListItem>
                          <ListItemText
                            primary="Weather"
                            secondary={selectedAssignment.requirements.weather}
                          />
                        </ListItem>
                      )}
                      {selectedAssignment.weatherMediaId && selectedAssignment.weatherMediaId.fileUrl && (
                        <ListItem>
                          <ListItemText
                            primary="Weather Briefing File"
                            secondary={
                              <Button
                                size="small"
                                component="a"
                                href={selectedAssignment.weatherMediaId.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View / Download
                              </Button>
                            }
                          />
                        </ListItem>
                      )}
                      {((selectedAssignment.tripKitMediaIds && selectedAssignment.tripKitMediaIds.length)
                        ? selectedAssignment.tripKitMediaIds
                        : (selectedAssignment.tripKitMediaId ? [selectedAssignment.tripKitMediaId] : [])
                      ).map((media, idx) => {
                        const url = typeof media === 'object' && media.fileUrl ? media.fileUrl : null;
                        const label = (typeof media === 'object' && media.fileUrl) ? (media.fileUrl.split('/').pop() || `TripKit ${idx + 1}`) : `TripKit ${idx + 1}`;
                        if (!url) return null;
                        return (
                          <ListItem key={media._id || idx}>
                            <ListItemText
                              primary={label}
                              secondary={
                                <Button
                                  size="small"
                                  component="a"
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View / Download
                                </Button>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Passengers & Weight
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Passenger Count"
                        secondary={(() => {
                          const sectorPassengers =
                            selectedAssignment.passengers?.length || 0;
                          const tripPassengers =
                            selectedAssignment.tripId?.passengers?.length || 0;
                          return sectorPassengers > 0
                            ? sectorPassengers
                            : tripPassengers;
                        })()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Total Weight"
                        secondary={`${
                          selectedAssignment.totalWeight ||
                          selectedAssignment.tripId?.totalWeight ||
                          0
                        } kg`}
                      />
                    </ListItem>
                    {((selectedAssignment.passengers &&
                      selectedAssignment.passengers.length > 0) ||
                      (selectedAssignment.tripId?.passengers &&
                        selectedAssignment.tripId.passengers.length > 0)) && (
                      <ListItem>
                        <ListItemText
                          primary="Passenger List"
                          secondary={
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                              {(
                                selectedAssignment.passengers &&
                                selectedAssignment.passengers.length > 0
                                  ? selectedAssignment.passengers
                                  : selectedAssignment.tripId?.passengers || []
                              ).map((p, idx) => (
                                <li key={idx}>
                                  {p.name} ({p.weight} kg)
                                  {p.specialRequirements &&
                                    ` - ${p.specialRequirements}`}
                                </li>
                              ))}
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {(selectedAssignment.remarks ||
              selectedAssignment.clearanceRemarks) && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Remarks
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <List dense>
                      {selectedAssignment.remarks?.assignment && (
                        <ListItem>
                          <ListItemText
                            primary="Assignment Remarks"
                            secondary={selectedAssignment.remarks.assignment}
                          />
                        </ListItem>
                      )}
                      {selectedAssignment.remarks?.requirements && (
                        <ListItem>
                          <ListItemText
                            primary="Requirements Remarks"
                            secondary={selectedAssignment.remarks.requirements}
                          />
                        </ListItem>
                      )}
                      {selectedAssignment.remarks?.crewResponse && (
                        <ListItem>
                          <ListItemText
                            primary="Crew Response"
                            secondary={selectedAssignment.remarks.crewResponse}
                          />
                        </ListItem>
                      )}
                      {selectedAssignment.remarks?.baTest && (
                        <ListItem>
                          <ListItemText
                            primary="BA Test Remarks"
                            secondary={selectedAssignment.remarks.baTest}
                          />
                        </ListItem>
                      )}
                      {(selectedAssignment.remarks?.clearance ||
                        selectedAssignment.clearanceRemarks) && (
                        <ListItem>
                          <ListItemText
                            primary="Clearance Remarks"
                            secondary={
                              selectedAssignment.remarks?.clearance ||
                              selectedAssignment.clearanceRemarks
                            }
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {selectedAssignment.clearanceNumber && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      ATC Clearance
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Clearance Number"
                          secondary={selectedAssignment.clearanceNumber}
                        />
                      </ListItem>
                      {selectedAssignment.clearanceRequestedAt && (
                        <ListItem>
                          <ListItemText
                            primary="Requested At"
                            secondary={formatDateTime(
                              selectedAssignment.clearanceRequestedAt,
                              null
                            )}
                          />
                        </ListItem>
                      )}
                      {selectedAssignment.clearanceSentAt && (
                        <ListItem>
                          <ListItemText
                            primary="Sent At"
                            secondary={formatDateTime(
                              selectedAssignment.clearanceSentAt,
                              null
                            )}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {(selectedAssignment.specialRequirements ||
              selectedAssignment.tripId?.specialRequirements) && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Special Requirements
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2">
                      {selectedAssignment.specialRequirements ||
                        selectedAssignment.tripId?.specialRequirements}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

// 2. Response Dialog
export const RespondDialog = ({
  open,
  onClose,
  selectedAssignment,
  responseForm,
  setResponseForm,
  onSubmit,
  formatRoute
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Respond to Assignment</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Sector: {selectedAssignment ? formatRoute(selectedAssignment) : 'N/A'}
      </Typography>
      <Divider sx={{ my: 2 }} />

      <TextField
        fullWidth
        select
        label="Decision"
        value={responseForm.decision}
        onChange={(e) =>
          setResponseForm((prev) => ({ ...prev, decision: e.target.value }))
        }
        margin="normal"
        required
        SelectProps={{
          native: true
        }}
      >
        <option value="ACCEPT">Accept</option>
        <option value="REJECT">Reject</option>
      </TextField>

      <TextField
        fullWidth
        label="Remarks"
        value={responseForm.remarks}
        onChange={(e) =>
          setResponseForm((prev) => ({ ...prev, remarks: e.target.value }))
        }
        margin="normal"
        multiline
        rows={3}
        placeholder="Add any comments or requirements..."
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onSubmit} variant="contained">
        Submit Response
      </Button>
    </DialogActions>
  </Dialog>
);

// 3. BA Failure Dialog
export const BaFailureDialog = ({
  open,
  onClose,
  selectedAssignment,
  baFailureForm,
  setBaFailureForm,
  onSubmit,
  formatRoute
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Report BA Issue</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Sector: {selectedAssignment ? formatRoute(selectedAssignment) : 'N/A'}
      </Typography>
      <Divider sx={{ my: 2 }} />
      <TextField
        fullWidth
        label="Remarks"
        value={baFailureForm.remarks}
        onChange={(e) => setBaFailureForm({ remarks: e.target.value })}
        margin="normal"
        multiline
        rows={4}
        placeholder="Describe the BA device issue or reason for failure"
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onSubmit} variant="contained" color="error">
        Submit Issue
      </Button>
    </DialogActions>
  </Dialog>
);

// 4. BA Upload Dialog
export const BaUploadDialog = ({
  open,
  onClose,
  selectedAssignment,
  formatRoute,
  onUpload
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Upload Breath Analyzer Video</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Sector: {selectedAssignment ? formatRoute(selectedAssignment) : 'N/A'}
      </Typography>
      <Divider sx={{ my: 2 }} />

      <BAUpload onUpload={onUpload} />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
    </DialogActions>
  </Dialog>
);

// 5. Signature Dialog
export const SignatureDialog = ({
  open,
  onClose,
  selectedAssignment,
  formatRoute,
  onSign
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Sign ATC Clearance</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Sector: {selectedAssignment ? formatRoute(selectedAssignment) : 'N/A'}
      </Typography>
      {selectedAssignment?.clearanceNumber && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Clearance Number: {selectedAssignment.clearanceNumber}
        </Typography>
      )}
      <Divider sx={{ my: 2 }} />

      <Typography variant="body2" gutterBottom>
        Please sign below to acknowledge receipt of ATC clearance:
      </Typography>

      <SignaturePad onSign={onSign} />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
    </DialogActions>
  </Dialog>
);

/**
 * Note: FlightLogDialog has been moved to CrewAssignments-part3.js
 * to keep file sizes manageable.
 */

